import { useEffect, useMemo } from 'react';
import type { ImportWizardState, WizardMode } from '../types';

const getStorageKey = (
  cabinetId: number | null,
  currentUserId: number,
  mode: WizardMode,
  targetCompanyId: number | null,
) => {
  const scope = cabinetId !== null && cabinetId > 0 ? String(cabinetId) : `u${currentUserId}`;
  return `acchub:importWizard:v3:${scope}:${mode}:${targetCompanyId ?? 0}`;
};

const isValidStoredState = (
  parsed: Partial<ImportWizardState>,
  mode: WizardMode,
  targetCompanyId: number | null,
): parsed is ImportWizardState =>
  parsed.version === 3 &&
  parsed.mode === mode &&
  (parsed.targetCompanyId ?? null) === targetCompanyId &&
  parsed.selectionsByConnectionId !== undefined &&
  parsed.attachTargetByRemoteId !== undefined &&
  parsed.allowCreateByRemoteId !== undefined &&
  parsed.mapping !== undefined &&
  parsed.run !== undefined &&
  parsed.conflicts !== undefined;

type UseWizardStorageResult = {
  storageKey: string;
  loadStoredState: (mode: WizardMode, targetCompanyId: number | null) => ImportWizardState | null;
  saveState: (state: ImportWizardState) => void;
  clearState: () => void;
};

export const useWizardStorage = (
  cabinetId: number | null,
  currentUserId: number,
  mode: WizardMode,
  targetCompanyId: number | null,
): UseWizardStorageResult => {
  const storageKey = useMemo(
    () => getStorageKey(cabinetId, currentUserId, mode, targetCompanyId),
    [cabinetId, currentUserId, mode, targetCompanyId],
  );

  const loadStoredState = (loadMode: WizardMode, loadTargetId: number | null): ImportWizardState | null => {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as Partial<ImportWizardState>;
      if (isValidStoredState(parsed, loadMode, loadTargetId)) {
        return parsed;
      }
    } catch {
      window.localStorage.removeItem(storageKey);
    }
    return null;
  };

  const saveState = (state: ImportWizardState) => {
    window.localStorage.setItem(storageKey, JSON.stringify(state));
  };

  const clearState = () => {
    window.localStorage.removeItem(storageKey);
  };

  return { storageKey, loadStoredState, saveState, clearState };
};

export const usePersistWizardState = (state: ImportWizardState, storageKey: string) => {
  useEffect(() => {
    window.localStorage.setItem(storageKey, JSON.stringify(state));
  }, [state, storageKey]);
};
